import { useEffect, useMemo, useState } from "react";
import { apiRequest, ApiError } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { Button } from "../../components/ui/Button.tsx";
import { Dialog } from "../../components/ui/Dialog.tsx";

interface FirmwareEntry {
  id: number;
  product_type: string;
  version: string;
  variant: string | null;
  public_url: string;
  file_hash: string;
  uploaded_at: string;
  notes: string | null;
}

interface BulkResult {
  pushed: number;
  total: number;
  results: { mac: string; ok: boolean; reason?: string }[];
}

interface Props {
  macs: string[];
  // Pre-counted product breakdown of the selection so the admin can see
  // which firmware will actually match.
  productBreakdown: Record<string, number>;
  onClose: () => void;
  onPushed: () => void;
}

export function BulkOtaDialog({ macs, productBreakdown, onClose, onPushed }: Props) {
  const { token } = useAuth();
  const [firmware, setFirmware] = useState<FirmwareEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<{ firmware: FirmwareEntry[] }>("/admin/firmware", { token })
      .then((res) => !cancelled && setFirmware(res.firmware))
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "load failed");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const selectedFw = firmware.find((f) => String(f.id) === selectedId);
  const matchCount = selectedFw ? (productBreakdown[selectedFw.product_type] ?? 0) : 0;
  const skipCount = macs.length - matchCount;

  // Group firmware by product so the dropdown is scannable when many products
  // are uploaded.
  const grouped = useMemo(() => {
    const map = new Map<string, FirmwareEntry[]>();
    for (const f of firmware) {
      const list = map.get(f.product_type) ?? [];
      list.push(f);
      map.set(f.product_type, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [firmware]);

  async function onPush() {
    if (!selectedFw) return;
    setRunning(true);
    setError(null);
    try {
      const res = await apiRequest<BulkResult>("/admin/devices/bulk-ota", {
        method: "POST",
        body: { macs, firmware_id: selectedFw.id },
        token,
      });
      setResult(res);
      onPushed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "push failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Dialog title="Bulk push OTA" onClose={onClose}>
      <div className="mb-4 rounded-[11px] border border-line bg-bg p-3 text-xs text-text2">
        <div className="text-text">
          {macs.length} device{macs.length === 1 ? "" : "s"} selected
        </div>
        <div className="mt-1">
          {Object.entries(productBreakdown).map(([p, n]) => (
            <span key={p} className="mr-3">
              <span className="text-text3">{p}:</span> <span className="text-text">{n}</span>
            </span>
          ))}
        </div>
      </div>

      <label className="block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text3">
        Firmware build
      </label>
      <select
        value={selectedId}
        onChange={(e) => setSelectedId(e.target.value)}
        disabled={loading || firmware.length === 0}
        className="mt-1.5 w-full rounded-[9px] border border-line bg-bg px-3 py-1.5 text-sm text-text outline-none transition focus:border-brand1 focus:ring-2 focus:ring-brand1/25 disabled:opacity-50"
      >
        <option value="">— select a build —</option>
        {grouped.map(([product, list]) => (
          <optgroup key={product} label={product}>
            {list.map((f) => (
              <option key={f.id} value={String(f.id)}>
                v{f.version}
                {f.variant ? ` (${f.variant})` : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {selectedFw ? (
        <div className="mt-3 rounded-[11px] border border-line bg-bg p-3 text-xs text-text2">
          <div>
            Matches{" "}
            <span className="text-text">
              {matchCount} {selectedFw.product_type}
            </span>{" "}
            device{matchCount === 1 ? "" : "s"}.
            {skipCount > 0 ? (
              <span className="ml-2 text-g2">{skipCount} will be skipped (product mismatch).</span>
            ) : null}
          </div>
          <div className="mt-1 break-all font-mono text-[11px] text-text3">
            sha256: {selectedFw.file_hash}
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-[9px] border border-g3/40 bg-g3/10 px-3 py-2 text-xs text-g3">
          {error}
        </div>
      ) : null}

      {result ? <BulkResultBox result={result} /> : null}

      <div className="mt-5 flex justify-end gap-2">
        <Button size="md" onClick={onClose}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result ? (
          <Button
            variant="primary"
            size="md"
            onClick={onPush}
            disabled={running || !selectedFw || matchCount === 0}
          >
            {running ? "Pushing…" : `Push to ${matchCount}`}
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}

function BulkResultBox({ result }: { result: BulkResult }) {
  const failed = result.results.filter((r) => !r.ok);
  return (
    <div className="mt-3 max-h-60 overflow-y-auto rounded-[9px] border border-brand1/40 bg-brand1/[0.08] px-3 py-2 text-xs text-brand2">
      <div className="font-semibold">
        Pushed {result.pushed} of {result.total} device{result.total === 1 ? "" : "s"}.
      </div>
      {failed.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-g2">
            {failed.length} failed / skipped — click for details
          </summary>
          <ul className="ml-4 mt-1 list-disc">
            {failed.map((r) => (
              <li key={r.mac} className="font-mono text-[11px]">
                {r.mac}
                {r.reason ? <span className="ml-2 text-text2">({r.reason})</span> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
